import {
    createConfig, createSelection, selectFrame, selectSize, selectKit, setDim, applyPreselect,
    frameOf, variantOf, items, totals, problems, formFields, overviewTitle,
} from './configurator-core.mjs';

/**
 * Alpine component `configurator` of the B2B bike page (templates/shop-b2b/bikes/_entry.html).
 * Data comes as JSON from <script id="configurator-data"> (payload of the ConfiguratorService, link selection,
 * texts); all logic lives in configurator-core.mjs. Replaces the stores role/master/price/base/build/variations.
 */
export function registerConfigurator(Alpine) {
    Alpine.data('configurator', () => {
        const data = JSON.parse(document.getElementById('configurator-data').textContent);
        const cfg = createConfig(data.payload);
        const sel = createSelection(cfg);
        applyPreselect(cfg, sel, data.preselect);

        const images = Object.freeze(data.images || []);
        const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

        return {
            images,
            imageIndex: 0,
            cfg,                    // frozen: not made reactive
            sel,
            title: data.title,
            t: data.texts || {},
            submitting: false,
            isFrameset: Boolean(data.isFrameset),
            contentsOpen: false,
            contentsSlug: null,     // null = the whole configuration, else only the kit of this slot
            contentsLoading: false,
            contents: {},           // kitId -> { title, image, parts[] } (loaded when the window opens)
            orderMode: 'delivery',  // webshop: delivery | pickup | inquiry (templates/_includes/configurator/order-paths.html)
            modes: { delivery: true, pickup: true, inquiry: true, primary: 'delivery' },  // what this market allows (helpers/OrderPaths.php)
            inquiry: { sending: false, done: false, error: '' },
            share: { busy: false, copied: false, error: '' },

            /** Aktion des Builds ("-50 % bis 31.12.") for the price block, '' without one. */
            get promoText() {
                const promo = this.cfg.promo;
                if (!promo) return '';
                const until = promo.until ? new Date(promo.until + 'T12:00:00').toLocaleDateString(document.documentElement.lang || 'de-CH', { day: 'numeric', month: 'numeric' }) : '';
                const amount = Number(promo.amount) || 0;
                const what = amount > 0
                    ? '-' + new Intl.NumberFormat(document.documentElement.lang || 'de-CH', { style: 'currency', currency: window.ARC8_CURRENCY || 'CHF', maximumFractionDigits: 0 }).format(amount)
                    : `-${Math.round(promo.percent * 10) / 10} %`;
                return what + (until ? ` ${this.t.until || 'bis'} ${until}` : '');
            },

            get frame() { return frameOf(this.cfg, this.sel); },
            get variant() { return variantOf(this.cfg, this.sel); },
            get items() { return items(this.cfg, this.sel); },
            get totals() { return totals(this.cfg, this.sel); },
            get problems() { return problems(this.cfg, this.sel); },
            get fields() { return formFields(this.cfg, this.sel); },
            get overviewTitle() { return overviewTitle(this.cfg, this.sel, this.title); },
            get ready() { return this.problems.length === 0; },

            init() {
                // another colour: back to the first picture of that colour
                this.$watch('sel.frameId', () => { this.imageIndex = 0; });
            },

            /** Why "add to cart" is disabled, for people and screen readers. */
            get hint() {
                const first = this.problems[0];
                if (!first) return '';
                return first.type === 'frame' ? this.t.chooseFrame : `${first.title}: ${this.t.unavailableCombination}`;
            },

            /**
             * Gallery of the studio pictures: those of the chosen frame colour first (this bike, then the model), then the
             * pictures without a colour; without any picture of the colour all of them, with a note in the caption.
             */
            get gallery() {
                if (!images.length) return [];
                const colour = norm(this.frame?.identifier);
                const exact = images.filter((i) => i.colour && norm(i.colour) === colour);
                const plain = images.filter((i) => !i.colour);
                return exact.length ? [...exact, ...plain] : [...plain, ...images.filter((i) => i.colour)];
            },
            get current() { return this.gallery[Math.min(this.imageIndex, this.gallery.length - 1)] || null; },
            step(delta) {
                const n = this.gallery.length;
                this.imageIndex = (this.imageIndex + delta + n) % n;
            },

            /** What the picture shows and where it differs from the choice: "Pictured: Eero – GRX 1×12, Emerald Green. Your selection differs in: Cockpit." */
            get caption() {
                const img = this.current;
                if (!img) return '';
                const t = this.t;
                const parts = [];
                const shown = [img.build || data.modelTitle, img.colour].filter(Boolean).join(', ');
                parts.push(`${t.shown}: ${shown}${img.size ? ` (${t.sizeShown} ${img.size})` : ''}.`);

                // a picture without a colour tag is not assumed to show the chosen colour
                const colour = norm(this.frame?.identifier);
                const colourMatches = Boolean(img.colour) && norm(img.colour) === colour;
                if (!colourMatches) {
                    parts.push(`${t.noPictureOfColour}: ${this.frame?.identifier}. ${img.colour ? '' : `${t.colourUnknown}.`}`.trim());
                }
                if (img.rank >= 2) {
                    parts.push(t.otherBike);
                } else {
                    // parts that differ from the standard equipment of the bike (what a picture of it shows)
                    const different = this.cfg.slots
                        .filter((slot) => {
                            const standard = slot.kits.find((kit) => kit.isDefault);
                            return standard && this.sel.kits[slot.slug] !== standard.kitId;
                        })
                        .map((slot) => t.slots?.[slot.slug] || slot.slug.replace(/-/g, ' '));
                    if (different.length) parts.push(`${t.deviations}: ${different.join(', ')}.`);
                    else if (colourMatches) parts.push(t.matches);
                }
                parts.push(t.pictureNote);
                return parts.join(' ');
            },

            /** The window "Included in your configuration": the OEM parts of the kits of the current selection. */
            async openContents(slug = null) {
                this.contentsSlug = slug;
                this.contentsOpen = true;
                const missing = this.items.filter((item) => !slug || item.slug === slug).map((item) => item.kitId).filter((id) => !(id in this.contents));
                if (!missing.length) return;
                this.contentsLoading = true;
                try {
                    const response = await fetch(`/actions/commerce-warehouse/configurator/contents?ids=${missing.join(',')}`, { headers: { Accept: 'application/json' } });
                    const result = await response.json();
                    this.contents = { ...this.contents, ...(result.kits || {}) };
                } catch (e) {
                    /* the window just stays empty */
                } finally {
                    this.contentsLoading = false;
                }
            },
            /** items of the selection with what they contain, for the window */
            get contentGroups() {
                return this.items
                    .filter((item) => !this.contentsSlug || item.slug === this.contentsSlug)
                    .map((item) => ({ item, kit: this.contents[item.kitId] || null }));
            },

            kitsOf(slug) { return this.cfg.slots.find((s) => s.slug === slug)?.kits || []; },
            variationOf(kitId) { return this.cfg.variations[kitId] || null; },
            dim(kitId, key) { return this.sel.dims[kitId]?.[key] ?? ''; },

            chooseFrame(id) { selectFrame(this.cfg, this.sel, id); },
            chooseSize(id) { selectSize(this.cfg, this.sel, id); },
            /** From the size finder (event arc8-size): the size with this label of the chosen frame, if it can be ordered. */
            chooseSizeLabel(label) {
                const variant = this.frame?.variants.find((v) => v.orderable && String(v.label).toLowerCase() === String(label).toLowerCase());
                if (variant) selectSize(this.cfg, this.sel, variant.id);
            },
            chooseKit(slug, id) { selectKit(this.cfg, this.sel, slug, id); },
            chooseDim(kitId, key, value) { setDim(this.sel, kitId, key, value); },

            /** Kits with variations of the chosen slots, for the "configuration notes" selects. */
            get variationItems() {
                return this.items.filter((item) => this.cfg.variations[item.kitId]);
            },

            price(item) {
                const mode = Alpine.store('priceMode')?.mode === 'uvp' ? 'uvp' : 'hek';
                return item[mode];
            },

            format(amount) {
                return new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
                    style: 'currency',
                    currency: window.ARC8_CURRENCY || 'CHF',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(Number(amount) || 0);
            },

            /** Webshop: what the main button does in this market (delivery | pickup | inquiry). */
            setOrderModes(modes) {
                this.modes = modes;
                this.orderMode = modes.primary;
            },

            /** "Copy configuration link": the server keeps the selection and answers with the address (<bike>?c=<hash>). */
            async copyLink(form) {
                if (this.share.busy || !this.ready) return;
                this.share.busy = true;
                this.share.error = '';
                const body = new FormData(form);
                body.set('action', 'commerce-warehouse/configurator/share');
                try {
                    const response = await fetch(window.location.pathname, {
                        method: 'POST',
                        body,
                        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    });
                    const result = await response.json().catch(() => ({}));
                    if (!response.ok || !result.url) {
                        this.share.error = result.message || result.error || this.t.linkFailed || 'Error';
                        return;
                    }
                    try {
                        await navigator.clipboard.writeText(result.url);
                    } catch (e) {
                        window.prompt(this.t.copyLink || 'Link', result.url);  // no clipboard access (http, old browser): let the visitor copy it
                    }
                    this.share.copied = true;
                    setTimeout(() => { this.share.copied = false; }, 3000);
                } catch (e) {
                    this.share.error = this.t.linkFailed || 'Error';
                } finally {
                    this.share.busy = false;
                }
            },

            /** One submit only (double click = two bikes in the cart); the server validates and builds the cart. */
            submit(event) {
                if (!this.ready || this.submitting) {
                    event.preventDefault();
                    return;
                }
                if (this.orderMode === 'inquiry') {
                    event.preventDefault();
                    this.sendInquiry(event.target);
                    return;
                }
                this.submitting = true;
            },

            /** Inquiry: the same form (selection + contact data) goes to the inquiry action instead of the cart. */
            async sendInquiry(form) {
                if (this.inquiry.sending || this.inquiry.done) return;
                this.inquiry.sending = true;
                this.inquiry.error = '';
                const body = new FormData(form);
                body.set('action', 'commerce-warehouse/configurator/inquiry');
                try {
                    const response = await fetch(window.location.pathname, {
                        method: 'POST',
                        body,
                        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    });
                    const result = await response.json().catch(() => ({}));
                    if (response.ok && result.success !== false) {
                        this.inquiry.done = true;
                    } else {
                        this.inquiry.error = result.message || result.error || this.t.inquiryFailed || 'Error';
                    }
                } catch (e) {
                    this.inquiry.error = this.t.inquiryFailed || 'Error';
                } finally {
                    this.inquiry.sending = false;
                }
            },
        };
    });
}
